import { useCopyToClipboard } from '@uidotdev/usehooks';
import { graphql } from 'client/gql';
import { useConfirm } from 'hooks/useConfirm';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { handleFileEntryDownload } from 'utils/download';
import { showSuccessToast } from 'utils/toasts';
import { downloadZipFile, getFileUrl } from '../utils/fileUtils';

const DeleteFileEntryMutation = graphql(`
  mutation BpDeleteFileEntry($where: FileEntryWhere!) {
    deleteFileEntries(where: $where) {
      nodesDeleted
      relationshipsDeleted
    }
  }
`);

export type BpFile = {
  uuid: string;
  filename: string;
  mediaType: string;
  size: number;
  downloadToken: string;
  publicDownloadToken: string | null;
};

export const useFiles = () => {
  const { t } = useTranslation();

  const [, deleteFile] = useMutation(DeleteFileEntryMutation);

  const [, copyToClipboard] = useCopyToClipboard();

  const { ConfirmationDialog, confirm: confirmDelete } = useConfirm({
    defaultTitle: t('delete.headline'),
    defaultMessage: t('delete.message', {
      type: t('files.title', { count: 1 }),
      context: 'female',
    }),
    defaultConfirmText: t('delete.delete'),
  });

  const [loadingUuid, setLoadingUuid] = useState<string | null>(null);

  async function handleDownload(file: BpFile) {
    setLoadingUuid(file.uuid);
    await handleFileEntryDownload({
      uuid: file.uuid,
      filename: file.filename,
      downloadToken: file.downloadToken,
    });
    setLoadingUuid(null);
  }

  async function handleDownloads(files: BpFile[], zipName?: string) {
    await downloadZipFile(files, zipName);
  }

  async function handleCopyLink(file: BpFile) {
    setLoadingUuid(file.uuid);
    await copyToClipboard(getFileUrl(file.uuid, file.publicDownloadToken ?? ''));
    showSuccessToast(t('common.copySuccess'));
    setLoadingUuid(null);
  }

  async function handleDelete(file: BpFile) {
    const confirm = await confirmDelete();
    if (!confirm) return;
    setLoadingUuid(file.uuid);
    await deleteFile({ where: { uuid: file.uuid } });
    setLoadingUuid(null);
  }

  async function handleDeleteFiles(files: BpFile[]) {
    const confirm = await confirmDelete({
      message: t('delete.message', {
        type: t('files.title', { count: 2 }),
        context: 'female',
      }),
    });
    if (!confirm) return;
    for (const file of files) {
      setLoadingUuid(file.uuid);
      await deleteFile({ where: { uuid: file.uuid } });
      setLoadingUuid(null);
    }
  }

  return {
    loadingUuid,
    handleDownload,
    handleDownloads,
    handleCopyLink,
    handleDelete,
    handleDeleteFiles,
    ConfirmationDialog,
  };
};
